// Motion tier — the three-way branch every animated component should use
// instead of a reduced-motion boolean.
//
// Before this hook, `useEffectiveReducedMotion()` was consumed as a binary and
// the "reduced" side of every branch meant *nothing happens*: RouteLayout
// returned a bare Outlet, WelcomePage collapsed each entrance to its final
// state. That's a cliff, not a ramp. A player on reduced motion should still
// get an authored sequence — same graphic payload, same beats, no travel,
// no spin, no parallax.
//
//   'full'     — the whole thing.
//   'lowPower' — same choreography, shorter, decorative layers dropped.
//                Device-capability driven, NOT a user preference.
//   'reduced'  — no travel/rotation/parallax. Cross-dissolve on the same
//                timeline skeleton. User-preference driven; always wins.

import { useEffectiveReducedMotion } from './useReducedMotion';
import type { MotionTier } from '../theme/motion';

export type { MotionTier };

/**
 * Static device-capability probe. Evaluated once at module load — these are
 * hardware facts that cannot change within a session, so re-checking them on
 * every render would be waste.
 *
 * The audience is LatAm-first and Android (Capacitor) is a shipping target, so
 * the mid-tier phone is the device that matters. Thresholds are deliberately
 * conservative: a false 'lowPower' costs a player some decoration, a false
 * 'full' costs them a janky first impression.
 */
const isLowPowerDevice: boolean = (() => {
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };

  // Explicit user/carrier data-saving signal — respect it as a general
  // "this device wants less" hint.
  if (nav.connection?.saveData === true) return true;

  // <= 2GB RAM. Chrome/Android only; undefined elsewhere, which we treat as
  // "no evidence of low power" rather than assuming the worst.
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true;

  // <= 4 logical cores. Catches the budget Android tier that deviceMemory
  // rounds generously for.
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4) {
    return true;
  }

  return false;
})();

/** Resolved motion tier. Reduced-motion preference always takes precedence. */
export function useMotionTier(): MotionTier {
  const reduced = useEffectiveReducedMotion();
  if (reduced) return 'reduced';
  return isLowPowerDevice ? 'lowPower' : 'full';
}

/** Non-hook read, for orchestration code that runs outside React render. */
export function readLowPowerDevice(): boolean {
  return isLowPowerDevice;
}
